import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Card, ErrorNote, Spinner, Timestamp } from '@/components/Common';
import { Modal } from '@/components/Modal';

interface VendorOwed {
  merchantId: string;
  merchantCode: string;
  merchantName: string;
  commissionRate: number;
  owedUsd: number;
  lastPaidAt: string | null;
}

/**
 * What's owed to each vendor for reselling. The platform pays vendors outside this system (cash
 * or bank transfer); this page only shows the running total since their last payout and lets an
 * admin mark it paid once they've actually done so.
 */
export function VendorPayoutsPage() {
  const [paying, setPaying] = useState<VendorOwed | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-payouts'],
    queryFn: () => api.get<{ vendors: VendorOwed[] }>('/api/admin/vendor-payouts'),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const vendors = data?.vendors ?? [];

  return (
    <div className="space-y-4">
      <Card title={`Vendor payouts — ${vendors.length}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-ink-200">
              <tr>
                <th className="th">Vendor</th>
                <th className="th text-right">Rate</th>
                <th className="th text-right">Owed (USD)</th>
                <th className="th">Last paid</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {vendors.map((v) => (
                <tr key={v.merchantId} className="hover:bg-ink-50">
                  <td className="td">
                    <span className="font-medium">{v.merchantName}</span>
                    <span className="ml-1 mono text-xs text-ink-500">{v.merchantCode}</span>
                  </td>
                  <td className="td text-right tabular">{(v.commissionRate * 100).toFixed(1)}%</td>
                  <td className="td text-right tabular font-medium">
                    ${v.owedUsd.toFixed(2)}
                  </td>
                  <td className="td text-xs text-ink-500">
                    {v.lastPaidAt ? <Timestamp value={v.lastPaidAt} /> : 'Never'}
                  </td>
                  <td className="td text-right">
                    <button type="button" className="btn-secondary" disabled={v.owedUsd <= 0}
                            onClick={() => setPaying(v)}>
                      Mark as paid
                    </button>
                  </td>
                </tr>
              ))}
              {vendors.length === 0 && (
                <tr><td className="td text-sm text-ink-500" colSpan={5}>
                  No vendors have a commission rate set yet — set one from Merchants.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {paying && <ConfirmPayoutModal vendor={paying} onClose={() => setPaying(null)} />}
    </div>
  );
}

function ConfirmPayoutModal({ vendor, onClose }: { vendor: VendorOwed; onClose: () => void }) {
  const queryClient = useQueryClient();

  const pay = useMutation({
    mutationFn: () => api.post(`/api/admin/vendor-payouts/${vendor.merchantId}/pay`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-payouts'] });
      onClose();
    },
  });

  return (
    <Modal title={`Mark ${vendor.merchantName} as paid`} onClose={onClose}>
      <p className="text-sm text-ink-600">
        This records that you've paid <span className="font-semibold">${vendor.owedUsd.toFixed(2)}</span>{' '}
        to {vendor.merchantName} outside the system — it does not send any money. It cannot be
        undone from here.
      </p>
      {pay.isError && <ErrorNote error={pay.error} />}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" disabled={pay.isPending}
                onClick={() => pay.mutate()}>
          {pay.isPending ? 'Recording…' : 'Confirm paid'}
        </button>
      </div>
    </Modal>
  );
}
